import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import LePackageUpgradeCardRoomDetails from './LePackageUpgradeCardRoomDetails'
import { useInclusionTravelPeriodFilter } from 'hooks/useInclusionTravelPeriodFilter'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Label from 'components/Luxkit/Label/Label'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import { InclusionsV2Section } from 'components/OfferPage/FlashHotel/InclusionsV2Section'
import { InclusionsSection } from 'components/OfferPage/FlashHotel/InclusionsSection'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import TextButton from 'components/Luxkit/Button/TextButton'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import Divider from 'components/Luxkit/Divider'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextLink from 'components/Luxkit/TextLink'
import { pluralizeToString } from 'lib/pluralize'

const PackageContainer = styled(Group)`
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  padding: ${rem(24)} ${rem(16)};
  min-width: 80vw;

  ${mediaQueryUp.sm} {
    min-width: ${rem(320)};
  }
`

const PackageHeaderDetails = styled(Group)`
  height: ${rem(100)};

  ${mediaQueryUp.sm} {
    height: ${rem(90)};
  }
`

const PackageBody = styled(Group)`
  flex: 1;
  overflow: hidden;

  &.scroll {
    overflow: scroll;
  }
`

const KeepPackageDesktopButton = styled(TextButton)`
  display: none;

  ${mediaQueryUp.sm} {
    display: block;
  }
`

const PackagePriceDetailsFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  height: ${rem(40)};
`

interface MappedProps {
  checkIn?: moment.Moment;
  duration?: number;
}

interface Props {
  pkg: App.Package;
  totalValue: number;
  priceDifference?: number;
  hasMoreInclusions?: boolean;
  isDifferentRoom?: boolean;
  onUpgrade?: () => void;
  onNoThanks?: () => void;
}

const imageProps: ImageParams = {
  xs: '5:3',
  md: '2:1',
  quality: 'good',
}

function LePackageUpgradeColumn({
  checkIn,
  duration,
  pkg,
  totalValue,
  priceDifference,
  hasMoreInclusions,
  isDifferentRoom,
  onUpgrade,
  onNoThanks,
}: Props & MappedProps) {
  const { roomType } = pkg
  const firstRoomImageId = roomType?.images?.[0]?.id

  const inclusionsV1 = useInclusionTravelPeriodFilter(pkg.inclusions, checkIn, checkIn?.clone().add(duration, 'days'))
  const bonusInclusionsV1 = useInclusionTravelPeriodFilter(pkg.bonusInclusions, checkIn, checkIn?.clone().add(duration, 'days'))
  const hasInclusionsV1 = pkg.inclusions && pkg.inclusions.length > 0
  const showInclusionV1 = hasInclusionsV1 && (inclusionsV1 || bonusInclusionsV1)

  const [viewMoreDetails, setViewMoreDetails] = useState(false)
  const onViewDetails = useCallback(() => { setViewMoreDetails(!viewMoreDetails) }, [viewMoreDetails])

  return (
    <PackageContainer direction="vertical" gap={12} fullHeight>
      <PackageHeaderDetails direction="vertical" gap={8}>
        {(!onUpgrade || isDifferentRoom || hasMoreInclusions) && (
          <LabelGroup>
            {!onUpgrade && <Label kind="secondary" variant="grey">Your package</Label>}
            {isDifferentRoom && <Label kind="tertiary" variant="highlight">Upgraded room</Label>}
            {hasMoreInclusions && <Label kind="tertiary" variant="highlight">More inclusions</Label>}
          </LabelGroup>
        )}
        <div>
          <Heading variant="heading6" colour="neutral-one">{pkg.name}</Heading>
          <Caption colour="neutral-three" variant="medium">{roomType?.name}</Caption>
        </div>
      </PackageHeaderDetails>

      <Divider kind="primary" />

      <PackageBody direction="vertical" gap={12} className={cn({ scroll: viewMoreDetails })}>
        {firstRoomImageId && (
          <ResponsiveImage
            {...imageProps}
            id={firstRoomImageId}
          />
        )}

        {roomType?.sizeSqm && <BodyText variant="small"><b>Room size:</b> {roomType.sizeSqm}m&sup2;</BodyText>}

        {showInclusionV1 && (
          <Group direction="vertical" gap={12}>
            {(inclusionsV1 && !bonusInclusionsV1) && <BodyText variant="small" weight="bold">Inclusions:</BodyText>}
            <InclusionsSection inclusions={inclusionsV1} bonusInclusions={bonusInclusionsV1} size="small" />
          </Group>
        )}
        {!hasInclusionsV1 && pkg.inclusionsV2 && (
          <Group direction="vertical" gap={12}>
            <BodyText variant="small" weight="bold">{pluralizeToString('inclusion', pkg.inclusionsV2.length)}:</BodyText>
            <InclusionsV2Section inclusions={pkg.inclusionsV2} size="small" />
          </Group>
        )}

        <LePackageUpgradeCardRoomDetails roomType={roomType} />
      </PackageBody>

      {!viewMoreDetails && (
        <TextLink onClick={onViewDetails}>
          <Caption variant="medium" weight="bold">
            View more details
          </Caption>
        </TextLink>
      )}

      <Divider kind="primary" />

      <PackagePriceDetailsFooter>
        <Group direction="vertical">
          {onUpgrade && priceDifference && (
            <Heading variant="heading6" colour="neutral-one">
              + <FormatCurrency value={priceDifference}/>
            </Heading>
          )}
          <BodyText variant="small" colour="neutral-three"><FormatCurrency value={totalValue}/> total for {pluralizeToString('night', pkg.duration)}</BodyText>
        </Group>
        {onUpgrade && <TextButton kind="primary" onClick={onUpgrade}>Upgrade</TextButton>}
        {onNoThanks && <KeepPackageDesktopButton kind="secondary" onClick={onNoThanks}>Keep package</KeepPackageDesktopButton>}
      </PackagePriceDetailsFooter>
    </PackageContainer>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState): MappedProps => ({
  checkIn: state.checkInDate,
  duration: state.duration,
}))(LePackageUpgradeColumn)
